/**
 * Echo Provider Component
 *
 * Manages session-level Laravel Echo connection for authenticated users.
 * Automatically connects on login and disconnects on logout.
 */

import { configureEcho, echo, echoIsConfigured } from '@laravel/echo-react';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from 'react';

import {
  type RawNotification,
  NOTIFICATION_CONSTANTS,
  JOB_PROGRESS_CONSTANTS,
  apiClient,
  claimReadOnlyError,
} from '@personal-trainer-app/shared';

import type {
  BindableConnection,
  EchoContextValue,
  EchoInstance,
  EchoProviderProps,
  PusherChannelLike,
  UnknownRecord,
} from './EchoProvider.types';
import type { EchoConnectionStatus } from './EchoProvider.types';

import { EchoJobProgressSessionBridge } from '@/components/providers/EchoJobProgressSessionBridge';
import { EchoNotificationSessionBridge } from '@/components/providers/EchoNotificationSessionBridge';
import { echoConfig } from '@/config';
import { useAuth } from '@/hooks/auth';
import { useNotificationSeed } from '@/hooks/notifications';
import { useNotificationStore } from '@/stores';

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function isPusherChannelLike(value: unknown): value is PusherChannelLike {
  return isRecord(value) && typeof value.name === 'string';
}

function getBindableConnectionFromConnector(
  connector: unknown
): BindableConnection | null {
  if (!isRecord(connector)) return null;

  // Expected shapes:
  // - connector.pusher.connection (Pusher protocol)
  // - connector.connection (fallback)
  const pusher = connector.pusher;
  if (isRecord(pusher) && isRecord(pusher.connection)) {
    const connection = pusher.connection as UnknownRecord;
    if (
      typeof connection.bind === 'function' &&
      typeof connection.unbind === 'function'
    ) {
      return connection as unknown as BindableConnection;
    }
  }

  const fallback = connector.connection;
  if (isRecord(fallback)) {
    if (
      typeof fallback.bind === 'function' &&
      typeof fallback.unbind === 'function'
    ) {
      return fallback as unknown as BindableConnection;
    }
  }

  return null;
}

const EchoContext = createContext<EchoContextValue | null>(null);

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

export const EchoProvider: React.FC<EchoProviderProps> = ({
  children,
  debug = false,
  onNotification,
}) => {
  // Web app is session-driven (cookie-based). Do not assume token auth in web.
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { setConnectionError, setConnectionStatus } = useNotificationStore();

  // Seed the live store with durable history so the bell badge and dropdown
  // reflect existing notifications, not just ones that arrive over the socket
  // this session. Independent of the WebSocket so history shows even if the
  // real-time connection fails.
  useNotificationSeed(isAuthenticated && !authLoading && !!user?.username);

  const [status, setStatus] = useState<EchoConnectionStatus>('disconnected');
  const [echoReady, setEchoReady] = useState(false);
  const echoRef = useRef<EchoInstance | null>(null);
  const unbindRef = useRef<(() => void) | null>(null);

  // Determine if Echo should be enabled (check auth state + kill switches).
  // The connection is shared: bring it up when EITHER real-time feature is on,
  // so job-progress works even when notifications are disabled. Each bridge is
  // gated by its own switch below. Both default off → no behaviour change.
  const realtimeEnabled =
    NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS ||
    JOB_PROGRESS_CONSTANTS.LIVE_JOB_PROGRESS;

  const shouldConnect =
    realtimeEnabled && isAuthenticated && !authLoading && !!user?.username;

  useEffect(() => {
    if (!debug) return;

    // This log is intentionally “gating-focused” so we can understand why the
    // user subscription bridge did/didn't mount.
    console.log('[EchoProvider] Subscription gate check', {
      liveNotifications: NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS,
      isAuthenticated,
      authLoading,
      username: user?.username ?? null,
      shouldConnect,
    });
  }, [debug, isAuthenticated, authLoading, user?.username, shouldConnect]);

  const handleStatusChange = useCallback(
    (status: EchoConnectionStatus) => {
      if (debug) {
        console.log(`[EchoProvider] Status changed: ${status}`);
      }

      // Handle connection errors
      if (status === 'error') {
        setConnectionError('Failed to connect to real-time notifications');
      }
    },
    [debug, setConnectionError]
  );

  const notificationListenersRef = useRef<
    Set<(notification: RawNotification) => void>
  >(new Set());

  const handleNotification = useCallback(
    (notification: RawNotification) => {
      if (debug) {
        console.log('[EchoProvider] Notification received:', notification);
      }

      // Call custom handler if provided
      onNotification?.(notification);
      notificationListenersRef.current.forEach((listener) =>
        listener(notification)
      );
    },
    [debug, onNotification]
  );

  const subscribeToNotifications = useCallback(
    (listener: (notification: RawNotification) => void) => {
      notificationListenersRef.current.add(listener);
      return () => {
        notificationListenersRef.current.delete(listener);
      };
    },
    []
  );

  const echoOptions = useMemo(() => {
    const baseAuth = (echoConfig.pusherConfig ?? {}).auth ?? {};
    const authEndpoint = echoConfig.authEndpoint ?? '/api/broadcasting/auth';

    const mergedAuthHeaders = {
      ...(baseAuth.headers ?? {}),
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };

    return {
      broadcaster: 'reverb',
      key: echoConfig.key,
      wsHost: echoConfig.host,
      wsPort: echoConfig.port,
      wssPort: echoConfig.port,
      forceTLS: echoConfig.forceTLS,
      enabledTransports: ['ws', 'wss'],
      authEndpoint,
      // Override Pusher's internal authorizer so we use Axios (which is already known-good in this app
      // for session-based auth + CSRF handling).
      authorizer: (channel: unknown) => {
        const channelName = isPusherChannelLike(channel) ? channel.name : '';

        return {
          authorize: async (
            socketId: string,
            callback: (error: boolean, data: unknown) => void
          ) => {
            try {
              const body = new URLSearchParams({
                socket_id: socketId,
                channel_name: channelName,
              }).toString();

              const response = await apiClient.post(authEndpoint, body, {
                withCredentials: true,
                headers: {
                  ...mergedAuthHeaders,
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
              });

              callback(false, response.data);
            } catch (err: unknown) {
              // The app subscribes on its own, so a read-only refusal (an
              // admin signed in as the member) is no notice of the admin's.
              claimReadOnlyError(err);
              if (debug) {
                console.warn('[EchoProvider] Broadcasting auth failed', {
                  authEndpoint,
                  channelName,
                  socketId,
                  err,
                });
              }
              callback(true, err);
            }
          },
        };
      },
      ...(echoConfig.cluster ? { cluster: echoConfig.cluster } : {}),
      ...(echoConfig.pusherConfig ?? {}),
      ...(Object.keys(mergedAuthHeaders).length > 0
        ? {
            auth: {
              ...baseAuth,
              headers: mergedAuthHeaders,
            },
          }
        : {}),
    };
  }, [debug]);

  const teardown = useCallback(() => {
    setEchoReady(false);

    try {
      unbindRef.current?.();
    } catch (e) {
      if (debug) {
        console.warn(
          '[EchoProvider] Failed to unbind connection listeners:',
          e
        );
      }
    } finally {
      unbindRef.current = null;
    }

    try {
      echoRef.current?.disconnect();
    } catch (e) {
      if (debug) {
        console.warn('[EchoProvider] Failed to disconnect Echo instance:', e);
      }
    } finally {
      echoRef.current = null;
    }

    setStatus('disconnected');
    setConnectionStatus('disconnected');
  }, [debug, setConnectionStatus]);

  // Configure Echo for the current session; tear down on logout / kill switch off.
  useEffect(() => {
    teardown();

    if (!shouldConnect) {
      if (debug) {
        console.log('[EchoProvider] Echo disabled (prerequisites not met)', {
          liveNotifications: NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS,
          isAuthenticated,
          authLoading,
          hasUsername: !!user?.username,
        });
      }
      return undefined;
    }

    // Kill switch: keep app stable without attempting to connect.
    if (!realtimeEnabled) {
      setConnectionError('Real-time updates disabled');
      return undefined;
    }

    try {
      setStatus('connecting');
      setConnectionStatus('connecting');

      // Configure singleton Echo with our explicit settings (Option B).
      configureEcho(echoOptions as never);

      // Force-create the instance so it connects before subscriptions mount.
      const instance = echo() as unknown as EchoInstance;
      echoRef.current = instance;
      setEchoReady(true);

      if (debug) {
        const headers =
          (
            echoOptions as unknown as {
              auth?: { headers?: Record<string, string> };
            }
          ).auth?.headers ?? {};
        console.log('[EchoProvider] Broadcasting auth configuration', {
          authEndpoint: echoOptions.authEndpoint,
          withCredentials: true,
          hasCsrfHeader:
            typeof headers['X-CSRF-TOKEN'] === 'string' ||
            typeof headers['X-XSRF-TOKEN'] === 'string',
        });
      }

      const connector = (instance as { connector?: unknown }).connector;
      const connection = getBindableConnectionFromConnector(connector);

      if (connection?.bind && connection?.unbind) {
        const bind = connection.bind.bind(connection) as (
          event: string,
          cb: (...args: unknown[]) => void
        ) => void;
        const unbind = connection.unbind.bind(connection) as (
          event: string,
          cb?: (...args: unknown[]) => void
        ) => void;

        const onConnected = () => {
          handleStatusChange('connected');
          setStatus('connected');
          setConnectionStatus('connected');
        };
        const onConnecting = () => {
          handleStatusChange('connecting');
          setStatus('connecting');
          setConnectionStatus('connecting');
        };
        const onDisconnected = () => {
          handleStatusChange('disconnected');
          setStatus('disconnected');
          setConnectionStatus('disconnected');
        };
        const onUnavailable = () => {
          handleStatusChange('reconnecting');
          setStatus('reconnecting');
          setConnectionStatus('reconnecting');
        };
        const onFailed = () => {
          setConnectionError('WebSocket connection failed');
          handleStatusChange('error');
          setStatus('error');
          setConnectionStatus('error');
        };
        const onError = (err: unknown) => {
          const message =
            err instanceof Error ? err.message : 'WebSocket connection error';
          setConnectionError(message);
          handleStatusChange('error');
          setStatus('error');
          setConnectionStatus('error');
        };

        bind('connected', onConnected);
        bind('connecting', onConnecting);
        bind('disconnected', onDisconnected);
        bind('unavailable', onUnavailable);
        bind('failed', onFailed);
        bind('error', onError);

        unbindRef.current = () => {
          unbind('connected', onConnected);
          unbind('connecting', onConnecting);
          unbind('disconnected', onDisconnected);
          unbind('unavailable', onUnavailable);
          unbind('failed', onFailed);
          unbind('error', onError);
        };
      }

      return () => teardown();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to initialize Echo';
      setConnectionError(message);
      handleStatusChange('error');
      setStatus('error');
      setConnectionStatus('error');
      setEchoReady(false);
      return () => teardown();
    }
  }, [
    authLoading,
    debug,
    echoOptions,
    handleStatusChange,
    isAuthenticated,
    realtimeEnabled,
    setConnectionError,
    setConnectionStatus,
    shouldConnect,
    teardown,
    user?.username,
  ]);

  // Log authentication status changes in debug mode
  useEffect(() => {
    if (debug) {
      console.log('[EchoProvider] Auth status changed:', {
        isAuthenticated,
        authLoading,
        username: user?.username,
        shouldConnect,
        notificationsEnabled: NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS,
      });

      if (!NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS) {
        console.log('[EchoProvider] Notifications disabled by kill switch');
      }
    }
  }, [debug, isAuthenticated, authLoading, user?.username, shouldConnect]);

  // Log connection status changes in debug mode
  useEffect(() => {
    if (debug) {
      console.log('[EchoProvider] Echo status:', {
        status,
        isConnected: status === 'connected',
        hasEchoInstance: !!echoRef.current,
        echoIsConfigured: echoIsConfigured(),
      });
    }
  }, [debug, status]);

  // Create context value
  const contextValue = useMemo<EchoContextValue>(
    () => ({
      echo: shouldConnect && echoReady ? echoRef.current : null,
      status: NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS
        ? status
        : 'disconnected',
      isConnected:
        NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS && status === 'connected',
      isConnecting:
        NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS &&
        (status === 'connecting' || status === 'reconnecting'),
      error: !NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS
        ? 'Notifications disabled'
        : status === 'error'
          ? 'Connection failed'
          : null,
      username: user?.username || null,
      isAuthenticated,
      subscribeToNotifications,
    }),
    [
      echoReady,
      shouldConnect,
      status,
      user?.username,
      isAuthenticated,
      subscribeToNotifications,
    ]
  );

  return (
    <EchoContext.Provider value={contextValue}>
      {shouldConnect &&
      echoReady &&
      user?.username &&
      NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS ? (
        <EchoNotificationSessionBridge
          username={user.username}
          debug={debug}
          onNotification={handleNotification}
        />
      ) : null}
      {shouldConnect &&
      echoReady &&
      user?.username &&
      JOB_PROGRESS_CONSTANTS.LIVE_JOB_PROGRESS ? (
        <EchoJobProgressSessionBridge username={user.username} debug={debug} />
      ) : null}
      {children}
    </EchoContext.Provider>
  );
};

// =============================================================================
// CONTEXT HOOK
// =============================================================================

/**
 * Hook to access Echo context
 */
export const useEchoContext = (): EchoContextValue => {
  const context = useContext(EchoContext);

  if (!context) {
    throw new Error('useEchoContext must be used within an EchoProvider');
  }

  return context;
};

// =============================================================================
// HELPER HOOKS
// =============================================================================

/**
 * Hook to get Echo connection status
 */
export const useEchoStatus = () => {
  const { status, isConnected, isConnecting, error } = useEchoContext();
  return { status, isConnected, isConnecting, error };
};

/**
 * Hook to get Echo instance (returns null if not authenticated)
 */
export const useEchoInstance = () => {
  const { echo, isAuthenticated } = useEchoContext();
  return isAuthenticated ? echo : null;
};

/**
 * Hook to subscribe to a channel (only when authenticated)
 */
export const useEchoChannel = (channelName: string) => {
  const echo = useEchoInstance();

  const subscribe = useMemo(() => {
    if (!echo || !channelName) return null;

    try {
      return echo.channel(channelName);
    } catch (error) {
      console.error(
        `[useEchoChannel] Failed to subscribe to ${channelName}:`,
        error
      );
      return null;
    }
  }, [echo, channelName]);

  return subscribe;
};

/**
 * Hook to subscribe to a private channel (only when authenticated)
 */
export const useEchoPrivateChannel = (channelName: string) => {
  const echo = useEchoInstance();

  const subscribe = useMemo(() => {
    if (!echo || !channelName) return null;

    try {
      return echo.private(channelName);
    } catch (error) {
      console.error(
        `[useEchoPrivateChannel] Failed to subscribe to private ${channelName}:`,
        error
      );
      return null;
    }
  }, [echo, channelName]);

  return subscribe;
};

/**
 * Hook to subscribe to a presence channel (only when authenticated)
 */
export const useEchoPresenceChannel = (channelName: string) => {
  const echo = useEchoInstance();

  const subscribe = useMemo(() => {
    if (!echo || !channelName) return null;

    try {
      if (typeof echo.presence === 'function') {
        return echo.presence(channelName);
      }
      if (typeof echo.join === 'function') {
        return echo.join(channelName);
      }
      throw new Error('Echo presence/join API not available');
    } catch (error) {
      console.error(
        `[useEchoPresenceChannel] Failed to subscribe to presence ${channelName}:`,
        error
      );
      return null;
    }
  }, [echo, channelName]);

  return subscribe;
};

export default EchoProvider;
